import {useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Button, Chip, IconButton, MenuItem, Stack, TextField, Typography} from '@mui/material';
import {Delete, ExpandMore} from '@mui/icons-material';
import {LibraryEquipmentChoiceDto, LibraryEquipmentRowDto} from '../../../../shared/dtos/comparable-library.dto';
import {equipmentTotal, lockedCost} from '../../../../shared/pricing/equipment-charges';
import {EquipmentRowDraft} from './figures';
import {MinutesField, formatMinutes, money} from './TimeFields';

/** The equipment types a work item's rows say were used: anything with actual time, or billed time when no actual time is recorded (the library's `equipmentUsed` fact). */
export function equipmentTypesUsed(rows: EquipmentRowDraft[], saved: LibraryEquipmentRowDto[], choices: LibraryEquipmentChoiceDto[]): string[] {
  const types = rows
    .filter(row => (row.actualDurationMinutes ?? row.durationMinutes) > 0)
    .map(row => choices.find(c => c.equipmentId === row.equipmentId)?.type ?? saved.find(s => s.equipmentId === row.equipmentId)?.type)
    .filter((type): type is string => !!type);
  return Array.from(new Set(types)).sort();
}

/**
 * What this work item's rows bill, minimum top-ups included, the way the
 * server's view sums them: the labor they displace on save. (A machine's
 * minimum is per job; rows of the same machine on other work items are not
 * in view here, so the preview is exact for a machine on this item alone.)
 */
export function equipmentBilled(rows: EquipmentRowDraft[], saved: LibraryEquipmentRowDto[]): number {
  return equipmentTotal(rows.map(row => {
    const savedRow = saved.find(s => s.equipmentId === row.equipmentId);
    return {
      equipmentId: row.equipmentId, durationMinutes: row.durationMinutes, hourlyRate: row.hourlyRate,
      minimumBillableMinutes: savedRow?.minimumBillableMinutes ?? 0, minimumCharge: row.minimumCharge, wholeDollars: savedRow?.wholeDollars ?? true,
    };
  }));
}

/**
 * The machines on a work item, their billed and actual time (legacy-record
 * editor). The work item's price stays: billed time moves labor time off
 * the work item at the locked rates, and removing a machine gives it back.
 * The rate is the row's locked one, or the catalog's today for a machine
 * added here; a new machine gets no minimum charge, since a minimum top-up
 * is charged once per job and would move the total.
 */
export function EquipmentRowsEditor({rows, saved, choices, onChange, disabled, laborCost, laborRatePerHour}: {
  rows: EquipmentRowDraft[]; saved: LibraryEquipmentRowDto[]; choices: LibraryEquipmentChoiceDto[];
  onChange: (rows: EquipmentRowDraft[]) => void; disabled: boolean;
  /** The work item's labor price now, so the editor can say what the machines will leave it. */
  laborCost?: number; laborRatePerHour?: number | null;
}) {
  const [adding, setAdding] = useState<number | ''>('');
  const update = (equipmentId: number, patch: Partial<EquipmentRowDraft>) =>
    onChange(rows.map(row => row.equipmentId === equipmentId ? {...row, ...patch} : row));
  const available = choices.filter(choice => !rows.some(row => row.equipmentId === choice.equipmentId));
  const add = () => {
    const choice = choices.find(c => c.equipmentId === adding);
    if (!choice) return;
    onChange([...rows, {equipmentId: choice.equipmentId, durationMinutes: 0, actualDurationMinutes: null, hourlyRate: choice.hourlyRate, minimumCharge: null}]);
    setAdding('');
  };
  const billedNow = equipmentBilled(rows, saved);
  const billedSaved = equipmentBilled(saved.map(s => ({equipmentId: s.equipmentId, durationMinutes: s.durationMinutes, actualDurationMinutes: s.actualDurationMinutes, hourlyRate: s.hourlyRate, minimumCharge: s.minimumCharge})), saved);
  const laborAfter = laborCost !== undefined ? laborCost - (billedNow - billedSaved) : undefined;
  const overrun = laborAfter !== undefined && laborAfter < 0;
  return (
    <Accordion defaultExpanded disableGutters elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore/>}><Typography variant="subtitle2">Equipment · billed and actual time</Typography></AccordionSummary>
      <AccordionDetails sx={{px: 0}}>
        <Stack spacing={2}>
          <Typography variant="body2" color={overrun ? 'error' : 'text.secondary'}>
            {laborCost !== undefined && laborAfter !== undefined
              ? `Labor ${money(laborCost)} now; ${overrun ? 'the machines would bill more than the work item\'s price' : `${money(laborAfter)} once these machines are billed`}${laborAfter !== undefined && laborRatePerHour && !overrun ? ` (${formatMinutes(Math.round(laborAfter / laborRatePerHour * 60))} of labor)` : ''}. The work item's price stays.`
              : 'Billed time moves labor time off the work item; its price stays.'}
          </Typography>
          {rows.length === 0 && <Typography variant="body2" color="text.secondary">No equipment on this work item.</Typography>}
          {rows.map(row => {
            const savedRow = saved.find(s => s.equipmentId === row.equipmentId);
            const choice = choices.find(c => c.equipmentId === row.equipmentId);
            const name = savedRow?.nickname ?? choice?.nickname ?? 'Equipment';
            const cost = lockedCost({
              equipmentId: row.equipmentId, durationMinutes: row.durationMinutes, hourlyRate: row.hourlyRate,
              minimumBillableMinutes: savedRow?.minimumBillableMinutes ?? 0, minimumCharge: row.minimumCharge, wholeDollars: savedRow?.wholeDollars ?? true,
            });
            return (
              <Stack key={row.equipmentId} spacing={1.5} sx={{p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, minWidth: 0}}
                     data-review-field={`figure-equipment-${row.equipmentId}`}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
                  <Typography fontWeight={600} noWrap sx={{flexGrow: 1, minWidth: 0}} title={name}>{name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{flexShrink: 0}}>{money(cost)}</Typography>
                  <IconButton size="small" aria-label={`Remove ${name}`} disabled={disabled} sx={{flexShrink: 0}}
                              onClick={() => onChange(rows.filter(r => r.equipmentId !== row.equipmentId))}><Delete fontSize="small"/></IconButton>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {row.hourlyRate !== null ? `${money(row.hourlyRate)}/h, ${savedRow ? 'locked on this work item' : 'the catalog rate today'}` : 'No hourly rate'}
                  {row.minimumCharge !== null ? ` · ${money(row.minimumCharge)} minimum per job` : ''}
                </Typography>
                {savedRow && (savedRow.origin === 'legacy_methods' || !savedRow.wholeDollars) && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {savedRow.origin === 'legacy_methods' && <Chip size="small" color="warning" label="Billed time guessed from an old method"/>}
                    {!savedRow.wholeDollars && <Chip size="small" label="Keeps cents"/>}
                  </Stack>
                )}
                <MinutesField label="Billed time" value={row.durationMinutes} disabled={disabled || row.hourlyRate === null} testId={`figure-equipment-billed-${row.equipmentId}`}
                              onChange={minutes => update(row.equipmentId, {durationMinutes: minutes ?? 0})}/>
                <MinutesField label="Actual time" value={row.actualDurationMinutes} disabled={disabled} testId={`figure-equipment-${row.equipmentId}`}
                              helper={row.durationMinutes > 0 ? 'Blank means the same as billed' : undefined}
                              onChange={minutes => update(row.equipmentId, {actualDurationMinutes: minutes})}/>
              </Stack>
            );
          })}
          <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
            <TextField select fullWidth size="small" label="Add equipment" disabled={disabled || available.length === 0}
                       value={adding} onChange={event => setAdding(Number(event.target.value))}>
              {available.map(choice => <MenuItem key={choice.equipmentId} value={choice.equipmentId}>
                {choice.nickname} · {choice.type}{choice.hourlyRate !== null ? ` · ${money(choice.hourlyRate)}/h` : ''}
              </MenuItem>)}
            </TextField>
            <Button size="small" variant="outlined" disabled={disabled || adding === ''} onClick={add} sx={{flexShrink: 0}} data-testid="add-equipment">Add</Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
